import * as React from 'react';

import { maybeParseZonedDateTime } from '@rematter/common/dateTime';
import {
  Button,
  cn,
  DataGrid,
  DataGridColumn,
  Pagination,
  type PaginationProps,
  Content,
  Footer,
  LocalizedDate,
  maybeEmptyInfo,
  Badge,
} from '@rematter/ui-web';
import { useAtom, useAtomValue } from 'jotai';
import { CheckCircle } from 'lucide-react';

import {
  ApprovalRequestRecord,
  ApprovalStatus,
} from '../../_graphql/approvalRequests';
import * as state from '../../_state/approvalList.state';
import { useTranslations } from '../../i18n/useTranslations';

import { ApprovalDetailDialog } from './ApprovalDetailDialog';

const APPROVAL_STATUS_COLORS: Record<
  ApprovalStatus,
  'success' | 'failure' | 'warning' | 'neutral'
> = {
  Pending: 'warning',
  Approved: 'success',
  Rejected: 'failure',
  Expired: 'neutral',
};

export const ApprovalListView: React.FC = () => {
  const t = useTranslations();

  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const [selectedApprovalId, setSelectedApprovalId] = useAtom(
    state.$selectedApprovalId,
  );
  const selectedStatus = useAtomValue(state.$selectedStatus);

  const {
    data,
    isLoading,
    error,
    refetch: refetchApprovals,
  } = useAtomValue(state.$remoteApprovals);

  const approvals = data?.approvals || [];
  const total = data?.total || 0;

  const handlePaginationChange: PaginationProps['onChange'] = ({
    limit: newLimit,
    offset: newOffset,
  }) => {
    setLimit(newLimit);
    setOffset(newOffset);
  };

  const handleRowClick = React.useCallback(
    ({ row }: { row: ApprovalRequestRecord }) => {
      setSelectedApprovalId(row.id);
    },
    [setSelectedApprovalId],
  );

  const handleCloseDialog = React.useCallback(() => {
    setSelectedApprovalId(null);
  }, [setSelectedApprovalId]);

  const handleApprovalSuccess = React.useCallback(() => {
    refetchApprovals();
    setSelectedApprovalId(null);
  }, [refetchApprovals, setSelectedApprovalId]);

  const columns = React.useMemo(() => {
    const columnDefs: DataGridColumn<ApprovalRequestRecord>[] = [
      {
        key: 'nodeLabel',
        header: t.approvals.approvalStep,
        cell: ({ row }) => (
          <div className="font-medium text-primary hover:underline">
            {maybeEmptyInfo(row.nodeLabel)}
          </div>
        ),
      },
      {
        key: 'automationId',
        header: t.approvals.automation,
        cell: ({ row }) => (
          <div className="text-sm text-neutral/80">
            {maybeEmptyInfo(row.automationId)}
          </div>
        ),
      },
      {
        key: 'status',
        header: t.approvals.status,
        cell: ({ row }) => {
          const color = APPROVAL_STATUS_COLORS[row.status];
          return (
            <Badge color={color}>{t.approvals.statusLabels[row.status]}</Badge>
          );
        },
      },
      {
        key: 'instructions',
        header: t.approvals.instructions,
        cell: ({ row }) => (
          <div className="max-w-md truncate text-sm text-neutral/60">
            {maybeEmptyInfo(row.instructions)}
          </div>
        ),
      },
      {
        key: 'requestedAt',
        header: t.approvals.requestedAt,
        cell: ({ row }) => (
          <div className="text-sm">
            {maybeEmptyInfo(row.requestedAt, (v) => (
              <LocalizedDate date={maybeParseZonedDateTime(v)} />
            ))}
          </div>
        ),
      },
      {
        key: 'expiresAt',
        header: t.approvals.expiresAt,
        cell: ({ row }) => (
          <div className="text-sm">
            {maybeEmptyInfo(row.expiresAt, (v) => (
              <LocalizedDate date={maybeParseZonedDateTime(v)} />
            ))}
          </div>
        ),
      },
      {
        key: 'actions',
        header: '',
        width: 120,
        pinned: 'right',
        cell: ({ row }) => {
          if (row.status !== 'Pending') {
            return null;
          }
          return (
            <div className="flex justify-end gap-2">
              <Button
                size="small"
                variant="outline"
                color="success"
                icon={<CheckCircle className="size-4" />}
                onPress={() => {
                  setSelectedApprovalId(row.id);
                }}
              >
                {t.approvals.review}
              </Button>
            </div>
          );
        },
      },
    ];

    return columnDefs.map((it) => ({
      ...it,
      onClick: it.key === 'actions' ? undefined : handleRowClick,
    }));
  }, [handleRowClick, setSelectedApprovalId, t]);

  const hasFiltersActive = Boolean(selectedStatus);

  const emptyPanelProps = {
    title: t.approvals.noApprovals,
    description: <span>{t.approvals.noApprovalsDescription}</span>,
    centered: true,
  };

  const emptyResultsPanelProps = {
    title: t.approvals.noApprovalsFiltered,
    description: t.approvals.noApprovalsFilteredDescription,
    centered: true,
  };

  if (error) {
    return (
      <div className="p-4 text-failure">
        {t.approvals.errorLoading(error.message)}
      </div>
    );
  }

  return (
    <div className={cn('@ApprovalListView', 'flex h-full flex-col')}>
      <Content className="h-full border-t border-neutral/10">
        <DataGrid<ApprovalRequestRecord>
          columns={columns}
          dataSource={approvals}
          rowKey="id"
          aria-label={t.approvals.ariaLabel}
          fullWidth
          isLoading={isLoading}
          emptyPanelProps={
            hasFiltersActive ? emptyResultsPanelProps : emptyPanelProps
          }
        />
      </Content>
      {total > limit && (
        <Footer>
          <Pagination
            limit={limit}
            offset={offset}
            total={total}
            onChange={handlePaginationChange}
          />
        </Footer>
      )}

      {selectedApprovalId && (
        <ApprovalDetailDialog
          approvalId={selectedApprovalId}
          isOpen={true}
          onClose={handleCloseDialog}
          onSuccess={handleApprovalSuccess}
        />
      )}
    </div>
  );
};
